var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ziphy-web-shared/basic/lib/loaders';
import { $modal } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { getLabResultsStatusComponent } from '@core/src/nedoShared/features/consultations/helpers/getLabResultsStatus';
import { getRequiredLabAttachments, getRequiredLabItemNames, hasRequiredAttachments, } from '@core/src/nedoShared/features/consultations/helpers/requiredAttachments';
import livequeueConsultationsStore from '@core/src/store/livequeueConsultationsStore';
import CloseIcon from '../../../assets/icons/close';
import { VerifyResultsModal } from '../VerifyResultsModal/VerifyResultsModal';
import styles from './styles.module.scss';
import { Dropzone } from './ui/Dropzone/Dropzone';
import { TestModalButtons } from './ui/TestModalButtons/TestModalButtons';
export const TestModal = ({ role, onClose, onSubmit, onFileSelected, onPrintPDF, type, images, consultation, onVerify, onDecline, resultStatus, attachment, showButtons, }) => {
    const { t } = useTranslation();
    const [uploadedFiles, setUploadedFiles] = useState((images || []).map((image, index) => ({
        id: `external-${index}-${image}`,
        file: new File([], `image-${index}.jpg`, { type: 'image/jpeg' }),
        preview: image, // Use the image URL directly as preview
    })));
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const isUpload = type === 'upload';
    const handleContainerClick = (e) => {
        e.stopPropagation();
    };
    const prevPreviewsRef = useRef(new Set());
    const formatUploadedFiles = (links) => {
        return links.map((link, index) => {
            const url = link.url;
            const isPdf = /\.pdf(\?|$)/i.test(url);
            const isJpg = /\.(jpg|jpeg)(\?|$)/i.test(url);
            const isPng = /\.png(\?|$)/i.test(url);
            const mime = isPdf
                ? 'application/pdf'
                : isJpg
                    ? 'image/jpeg'
                    : isPng
                        ? 'image/png'
                        : 'application/octet-stream';
            const filename = isPdf
                ? `file-${index}.pdf`
                : isJpg
                    ? `image-${index}.jpg`
                    : isPng
                        ? `image-${index}.png`
                        : `file-${index}`;
            return {
                id: `downloaded-${index}-${url}`,
                file: new File([], filename, { type: mime }),
                preview: url,
            };
        });
    };
    const loadDownlaodLinks = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!consultation)
            return;
        try {
            setLoading(true);
            const fetchFns = images.map((img) => livequeueConsultationsStore.getAttachmentDownloadUrl(img, consultation._id));
            const downloadLinks = yield Promise.all(fetchFns);
            const formattedFiles = formatUploadedFiles(downloadLinks);
            setUploadedFiles(formattedFiles);
        }
        catch (err) {
            console.error('Error loading download links:', err);
        }
        finally {
            setLoading(false);
        }
    }), []);
    useEffect(() => {
        if (images.length && type === 'result') {
            loadDownlaodLinks();
        }
    }, [images, type]);
    const handleFileSelect = useCallback((files) => {
        if (!files)
            return;
        Array.from(files).forEach((file) => {
            if (file.type.startsWith('image/') || file.type === 'application/pdf') {
                const id = `${file.name}-${Date.now()}`;
                const preview = URL.createObjectURL(file);
                setUploadedFiles((prev) => [...prev, { id, file, preview }]);
            }
        });
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileSelect(e.dataTransfer.files);
        onFileSelected(e.dataTransfer.files);
    }, [handleFileSelect]);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);
    const handleFileInputChange = useCallback((e) => {
        const files = e.target.files;
        handleFileSelect(files);
        onFileSelected(files);
        e.target.value = '';
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [handleFileSelect]);
    const handleUploadClick = useCallback(() => {
        var _a;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, []);
    const handleRemoveFile = useCallback((id) => {
        setUploadedFiles((prev) => {
            const fileToRemove = prev.find((f) => f.id === id);
            if (fileToRemove && fileToRemove.preview.startsWith('blob:')) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            return prev.filter((f) => f.id !== id);
        });
    }, []);
    useEffect(() => {
        const prev = prevPreviewsRef.current;
        const next = new Set(uploadedFiles.map((f) => f.preview));
        prev.forEach((url) => {
            if (!next.has(url) && url.startsWith('blob:')) {
                try {
                    URL.revokeObjectURL(url);
                }
                catch (e) { }
            }
        });
        prevPreviewsRef.current = next;
    }, [uploadedFiles]);
    useEffect(() => {
        return () => {
            prevPreviewsRef.current.forEach((url) => {
                if (url.startsWith('blob:')) {
                    try {
                        URL.revokeObjectURL(url);
                    }
                    catch (e) { }
                }
            });
        };
    }, []);
    const getSafeContentType = (file) => {
        const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
        if (allowed.includes(file.type))
            return file.type;
        return 'application/octet-stream';
    };
    const handleSubmit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!consultation)
            return;
        setUploadError(null);
        setIsUploading(true);
        try {
            const reqs = getRequiredLabAttachments(consultation);
            const currentReq = reqs[0];
            const attachmentRequirementId = (_a = currentReq === null || currentReq === void 0 ? void 0 : currentReq.id) !== null && _a !== void 0 ? _a : null;
            for (const f of uploadedFiles) {
                yield livequeueConsultationsStore.uploadConsultationAttachments(consultation._id, {
                    attachment_type: 'lab_results',
                    content_type: getSafeContentType(f.file),
                    attachment_requirement_id: attachmentRequirementId,
                });
                const { url, fields, id } = livequeueConsultationsStore.attachmentsInProgress || {};
                if (!url || !fields || !id) {
                    setUploadError('Upload init failed');
                }
                const formData = new FormData();
                Object.entries(fields).forEach(([k, v]) => formData.append(k, String(v)));
                const policyContentType = fields['Content-Type'] || getSafeContentType(f.file);
                const fileForUpload = new File([f.file], f.file.name || 'upload', {
                    type: policyContentType,
                });
                formData.append('file', fileForUpload);
                const res = yield fetch(url, {
                    method: 'POST',
                    body: formData,
                });
                if (!res.ok) {
                    try {
                        yield livequeueConsultationsStore.cancelAttachmentUpload(id, consultation._id);
                    }
                    catch (e) { }
                    let details = '';
                    try {
                        details = yield res.text();
                    }
                    catch (e) { }
                    setUploadError('File upload failed');
                    return;
                }
                yield livequeueConsultationsStore.confirmAttachmentUpload(id, consultation._id);
            }
            onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(uploadedFiles.map((uf) => uf.file));
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
        catch (e) {
            setUploadError((e === null || e === void 0 ? void 0 : e.message) || 'Upload error');
        }
        finally {
            setIsUploading(false);
        }
    }), [consultation, uploadedFiles, onSubmit, onClose]);
    const handleVerifySuccess = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        onVerify === null || onVerify === void 0 ? void 0 : onVerify();
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }), [onVerify, onClose]);
    const handleDeclineSuccess = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        onDecline === null || onDecline === void 0 ? void 0 : onDecline();
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }), [onDecline, onClose]);
    const handleDecline = useCallback(() => {
        $modal.add(VerifyResultsModal, {
            data: {
                title: t('label.verify_results_title'),
                message: t('label.decline_results_message'),
            },
            type: 'decline',
            submitButtonText: t('btn.decline'),
            onDone: handleDeclineSuccess,
        });
    }, [handleDeclineSuccess]);
    const handleVerify = useCallback(() => {
        $modal.add(VerifyResultsModal, {
            data: {
                title: t('label.verify_results_title'),
                message: t('label.verify_results_message'),
            },
            type: 'verify',
            submitButtonText: t('btn.verify'),
            onDone: handleVerifySuccess,
        });
    }, [handleVerifySuccess]);
    if (isLoading) {
        return (_jsxs("div", Object.assign({ className: styles.wrapper }, { children: [_jsx("div", { className: styles.bg, onClick: onClose }), _jsx("div", Object.assign({ className: styles.container, onClick: handleContainerClick }, { children: _jsx(Loader, {}) }))] })));
    }
    return (_jsxs("div", Object.assign({ className: styles.wrapper }, { children: [_jsx("div", { className: styles.bg, onClick: onClose }), _jsxs("div", Object.assign({ className: styles.container, onClick: handleContainerClick }, { children: [_jsxs("div", Object.assign({ className: styles.headerBlock }, { children: [_jsxs("div", Object.assign({ className: styles.header }, { children: [_jsx("h3", Object.assign({ className: styles.text }, { children: type === 'upload' ? 'Upload Test Results' : 'Test Results' })), _jsx("div", Object.assign({ className: styles.close, onClick: onClose }, { children: _jsx(CloseIcon, {}) }))] })), resultStatus &&
                                resultStatus !== 'pending' &&
                                getLabResultsStatusComponent(resultStatus, {
                                    wrapper: styles.status,
                                    icon: styles.icon,
                                    text: styles.text,
                                })] })), _jsxs("div", Object.assign({ className: styles.content }, { children: [_jsxs("div", Object.assign({ className: styles.contentHeader }, { children: [getRequiredLabItemNames(consultation).map((name, idx) => (_jsx("span", Object.assign({ className: styles.contentHeaderText }, { children: name }), idx))), !hasRequiredAttachments(consultation) && (_jsx("span", Object.assign({ className: styles.contentHeaderText }, { children: "No Requirement Test Results" })))] })), _jsx(Dropzone, { onDrop: isUploading ? undefined : handleDrop, onDragOver: isUploading ? undefined : handleDragOver, onDragLeave: isUploading ? undefined : handleDragLeave, onClick: isUploading ? undefined : handleUploadClick, uploadedFiles: uploadedFiles, isDragOver: isDragOver, onRemoveFile: isUploading ? undefined : handleRemoveFile, canRemoveFile: isUpload && !isUploading, canAddFile: isUpload && !isUploading, isUpload: isUpload }), uploadError && _jsx("div", Object.assign({ className: styles.error }, { children: uploadError })), showButtons && (_jsx(TestModalButtons, { role: role, uploadedFiles: uploadedFiles, isUploading: isUploading, handleSubmit: handleSubmit, onPrintPDF: onPrintPDF, onVerify: handleVerify, onDecline: handleDecline }))] }))] })), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: ".jpg,.jpeg,.png,.pdf", onChange: handleFileInputChange, style: { display: 'none' } })] })));
};
